import { DataAssetTreeDirectoryResponse } from "../../apiClient/models/DataAssetTreeDirectoryResponse";
import { DataAssetWithManifestAndRealAssetDeploymentResponse } from "../../apiClient/models/DataAssetWithManifestAndRealAssetDeploymentResponse";
import { ManifestType } from "../../apiClient";
import { AiFillFolder } from "react-icons/ai";
import {
  ADDENDUM_SUFFIX,
  ADDENDUM_TYPES,
  ARCHIVE_SUFFIX,
  ARCHIVE_TYPES,
} from "./constants";
import { FileSystemItem } from "./types";
import { getFileIconData } from "../../utils/cardUtil";

export const ensurePathExists = (
  node: FileSystemItem,
  pathSegments: string[],
  fullPath: string[] = pathSegments
): FileSystemItem => {
  if (pathSegments.length === 0) return node;

  const [head, ...rest] = pathSegments;
  const children = node.children || [];
  let targetChild = children.find((child) => child.name === head);

  // Create missing folder if it doesn't exist
  if (!targetChild) {
    // Calculate how deep we are in the path to build the correct full path
    const depth = fullPath.length - pathSegments.length;
    const pathSoFar = fullPath.slice(0, depth + 1).join("/");
    targetChild = {
      name: head,
      path: pathSoFar,
      type: "folder",
    };
    return {
      ...node,
      children: [...children, ensurePathExists(targetChild, rest, fullPath)],
    };
  }

  // Recursively ensure rest of path exists
  const updatedChildren = children.map((child) => {
    if (child.name !== head) return child;
    return ensurePathExists(child, rest, fullPath);
  });

  return { ...node, children: updatedChildren };
};

export const insertChildrenAtPath = (
  node: FileSystemItem,
  pathSegments: string[],
  children: FileSystemItem[]
): FileSystemItem => {
  if (pathSegments.length === 0) {
    return { ...node, children };
  }
  if (!node.children) return node;
  const [head, ...rest] = pathSegments;
  const updated = node.children.map((child) => {
    if (child.name !== head) return child;
    return insertChildrenAtPath(child, rest, children);
  });
  return { ...node, children: updated };
};

export const ensureArchiveRoots = (cruise: FileSystemItem): FileSystemItem => {
  const archiveName = `${cruise.name}${ARCHIVE_SUFFIX}`;
  const addendumName = `${cruise.name}${ADDENDUM_SUFFIX}`;
  const children = cruise.children || [];
  const nextChildren = [...children];

  if (!nextChildren.find((c) => c.name === archiveName)) {
    nextChildren.push({
      name: archiveName,
      path: `${cruise.name}/${archiveName}`,
      type: "folder",
      // Don't set children: [] - leave undefined so folder doesn't appear empty
      readOnly: true,
    });
  }

  if (!nextChildren.find((c) => c.name === addendumName)) {
    nextChildren.push({
      name: addendumName,
      path: `${cruise.name}/${addendumName}`,
      type: "folder",
      children: [],
    });
  }

  return { ...cruise, children: nextChildren };
};

export const manifestTypesForPath = (pathSegments: string[]) => {
  const manifestPart = pathSegments[1] || "";
  return manifestPart.includes(ADDENDUM_SUFFIX)
    ? ADDENDUM_TYPES
    : ARCHIVE_TYPES;
};

export const parentPathForApi = (pathSegments: string[]) => {
  if (pathSegments.length <= 2) return "";
  return pathSegments.slice(2).join("/");
};

export const resolveLinkedCruisePath = (
  queryPath: string[],
  linkedCruiseId?: string,
  linkedManifestType?: ManifestType
): string[] => {
  if (!linkedCruiseId) return [];
  if (queryPath.length > 0) return [linkedCruiseId, ...queryPath];

  if (linkedManifestType) {
    const suffix = ADDENDUM_TYPES.includes(linkedManifestType)
      ? ADDENDUM_SUFFIX
      : ARCHIVE_SUFFIX;
    return [linkedCruiseId, `${linkedCruiseId}${suffix}`];
  }

  return [linkedCruiseId];
};

// Utility function to extract filename from path
export const extractFileName = (path: string | null | undefined): string => {
  if (!path) return "";
  const normalized = path.replace(/\/+$/, ""); // remove trailing slashes
  const lastSlash = normalized.lastIndexOf("/");
  return lastSlash === -1 ? normalized : normalized.substring(lastSlash + 1);
};

// Get relative location path from current directory
export const getRelativeLocation = (
  itemPath: string | null | undefined,
  currentPath: string[]
): string => {
  if (!itemPath) return "/";

  // Get the directory path (everything except the file/folder name)
  const pathParts = itemPath.split("/");
  const directoryPath = pathParts.slice(0, -1).join("/");

  // Current path joined (e.g., "OXR20250326/OXR20250326_cruise_archive/Vessels")
  const currentPathStr = currentPath.join("/");

  // If directory is exactly current path, show root
  if (directoryPath === currentPathStr) {
    return "/";
  }

  // If directory starts with current path, show relative path
  if (directoryPath.startsWith(currentPathStr + "/")) {
    const relativePath = directoryPath.substring(currentPathStr.length + 1);
    return "/" + relativePath;
  }

  // If directory starts with current path (but no slash), show empty
  if (directoryPath.startsWith(currentPathStr)) {
    return "/";
  }

  // Fallback to absolute path
  return "/" + directoryPath;
};

// Map directory responses to FileSystemItems
export const mapDirectoriesToFileItems = (
  directories: DataAssetTreeDirectoryResponse[],
  basePath: string[], // e.g., ["OXR20250326", "OXR20250326_cruise_archive", "Vessel"]
  isArchive: boolean
): FileSystemItem[] => {
  // Prefix only with cruise + archive/addendum (first 2 segments)
  // API path already includes the full path from deployment root
  const prefix = basePath.slice(0, 2).join("/");
  const items: FileSystemItem[] = [];

  directories.forEach((dir) => {
    // Skip items with null/empty paths
    if (!dir.path) return;

    const name = extractFileName(dir.path);
    const fullPath = prefix ? `${prefix}/${dir.path}` : dir.path;

    items.push({
      name,
      path: fullPath,
      type: "folder",
      size: dir.size ?? undefined,
      modified: dir.asset_modified_dt ?? undefined,
      realAssetDeploymentPublicId:
        dir.real_asset_deployment_public_id ?? undefined,
      children: [],
      readOnly: isArchive,
      childCount: dir.child_count,
    });
  });

  return items;
};

// Map file responses to FileSystemItems
export const mapFilesToFileItems = (
  files: DataAssetWithManifestAndRealAssetDeploymentResponse[],
  basePath: string[], // e.g., ["OXR20250326", "OXR20250326_cruise_archive", "Vessel"]
  isArchive: boolean
): FileSystemItem[] => {
  // Prefix only with cruise + archive/addendum (first 2 segments)
  // API path already includes the full path from deployment root
  const prefix = basePath.slice(0, 2).join("/");
  const items: FileSystemItem[] = [];

  files.forEach((file) => {
    // Skip items with null/empty paths
    if (!file.path) return;

    const name = extractFileName(file.path);
    const fullPath = prefix ? `${prefix}/${file.path}` : file.path;

    const deploymentPublicId = file.real_asset_deployments?.[0]?.public_id;

    items.push({
      name,
      path: fullPath,
      type: "file",
      dataAssetPublicId: file.public_id ?? undefined,
      realAssetDeploymentPublicId: deploymentPublicId ?? undefined,
      size: file.size ?? undefined,
      created: file.asset_created_dt ?? undefined,
      modified: file.asset_updated_dt ?? undefined,
      uploaded: file.asset_created_dt ?? undefined,
      readOnly: isArchive,
    });
  });

  return items;
};

export const getChildCount = (item: FileSystemItem) => {
  // If childCount is explicitly set, use it
  if (typeof item.childCount === "number") return item.childCount;

  // If children array exists, count it
  if (item.children !== undefined) return item.children.length;

  // If neither exists, return undefined (folder not yet loaded, not empty)
  return undefined;
};

export const getFileIcon = (fileName: string) => {
  const { Icon, className, size } = getFileIconData(fileName, {
    variant: "browse",
  });
  return <Icon className={className} size={size} />;
};

export const getBrowseItemIcon = (
  item: Pick<FileSystemItem, "name" | "type" | "cruiseId">,
  options: { folderSize?: number; folderClassName?: string } = {}
) => {
  const { folderSize = 20, folderClassName = "text-yellow-500" } = options;

  if (item.cruiseId || item.type === "folder") {
    return <AiFillFolder className={folderClassName} size={folderSize} />;
  }

  return getFileIcon(item.name);
};
